import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  useGetUserSubscriptionListQuery,
  useGetVerifyCompanyPaymentFetchQuery,
} from "../../../../store/Company/companyApiSlice";
import { pickActiveSub } from "../../../../utils/pickActiveSubscription";
import { payableRows } from "./billingFormat";

/* What the Billing screen knows. */

/* Transcribed precedence. Verified-company payments lead when there are any;
   the subscription list is appended, or stands alone. Both empty gives an
   empty list rather than undefined, so callers need no second guard. */
export const composeRows = (subscriptionData, verifyCompanyData) => {
  let combined;
  if (subscriptionData && verifyCompanyData?.length > 0) {
    combined = verifyCompanyData.concat(subscriptionData);
  } else if (verifyCompanyData?.length > 0) {
    combined = verifyCompanyData;
  } else if (subscriptionData) {
    combined = subscriptionData;
  }
  if (!combined) return [];
  /* Copied before sorting: the arrays come from the RTK Query cache and must
     not be sorted in place. */
  return combined
    .slice()
    .sort((a, b) => new Date(b?.date_of_start) - new Date(a?.date_of_start));
};

/* Transcribed: a subscription row whose invoice_url is the *string* "null" and is not freemium hides both the plan's action button and the whole history table. */
export const shouldHidePlan = (subscriptionData, verifyCompanyData) => {
  if (verifyCompanyData?.length > 0) return false;
  return Boolean(
    subscriptionData?.find(
      (i) => i?.invoice_url === "null" && i?.subscription_type !== "freemium"
    )
  );
};

/* Which row the plan card shows. */
export const surfacedSub = (subscriptionData, today) => {
  const active = pickActiveSub(subscriptionData, today);
  const cancelledVerified = !active
    ? subscriptionData?.find(
        (i) =>
          i?.subscription_type === "verified" &&
          (i?.recurring_status === "cancelled" || i?.status === "inactive")
      )
    : null;
  return active || cancelledVerified || undefined;
};

export const useBillingData = () => {
  const { userInfo, token } = useSelector((state) => state.auth);
  /* The live screen reads `userInfo?.individual[0]` — which throws when a user
     has no individual at all. Guarded here; the loaded case is identical. */
  const individualID = userInfo?.individual?.[0]?.individualID;

  /* Transcribed, argument shape included — the endpoint does not need
     individualID, but changing its signature is a store change. */
  const { data: subscriptionData, refetch: refetchSubscription } =
    useGetUserSubscriptionListQuery(
      { individualID, token },
      { refetchOnMountOrArgChange: true }
    );

  const { data: verifyCompanyData, refetch: refetchVerified } =
    useGetVerifyCompanyPaymentFetchQuery({ individualID, token });

  /* Transcribed: both lists are refetched when the session user changes. */
  useEffect(() => {
    refetchSubscription();
    refetchVerified();
  }, [userInfo, refetchSubscription, refetchVerified]);

  const rows = useMemo(
    () => composeRows(subscriptionData, verifyCompanyData),
    [subscriptionData, verifyCompanyData]
  );

  const activeSub = useMemo(
    () => surfacedSub(subscriptionData, new Date()),
    [subscriptionData]
  );

  const hidePlan = useMemo(
    () => shouldHidePlan(subscriptionData, verifyCompanyData),
    [subscriptionData, verifyCompanyData]
  );

  /* State, not a memo: a successful cancellation writes the server's message
     here, which is what disables the control afterwards. */
  const [cancelled, setCancelled] = useState(undefined);
  useEffect(() => {
    setCancelled(activeSub?.recurring_status);
  }, [activeSub?.recurring_status]);

  return {
    token,
    /* The history table's rows: freemium never appears there. */
    rows: payableRows(rows),
    /* The unfiltered set, only for the "is there anything at all" question the
       live screen asks before choosing the empty state. */
    allRows: rows,
    subscriptionData,
    activeSub,
    hidePlan,
    cancelled,
    setCancelled,
  };
};

export default useBillingData;
